/**
 * v0 → v1 content-URL mapping for B2C portrait images.
 *
 * The backend emits v0 image URLs (`/api/get_image/{bucket}/{uuid}`,
 * `/api/get_upscaled_image/{bucket}/{uuid}`) which buffer the whole S3
 * object before the first byte. Their `/api/v1/content/images/{uuid}` twin
 * streams and honors byte ranges, so `fetchParallelBlob` can fan out chunks
 * — and a plain `<img>` stops paying the full-buffer TTFB. The bucket path
 * segment is dropped on purpose: the backend resolves it from the stored
 * S3 link.
 *
 * Unrecognized URLs (S3/CDN/external) pass through unchanged and v1 URLs
 * don't match the v0 patterns, so the mapping is idempotent.
 *
 * BOTH v0 shapes resolve to `variant=auto`. ENTR renders every content URL
 * into an `<img>`, and `<img>` has no access to the response status: an
 * `upscaled` miss is indistinguishable from a network failure, so
 * `imageRetry` burns its four attempts and leaves a permanently broken tile.
 * `auto` prefers the upscaled row and falls back to the generated one in a
 * single request, which is the only resolution ENTR ever wants — it has no
 * archival path that needs upscaled-or-nothing. This is the one deliberate
 * divergence from the B2C copy at
 * `frontend/src/shared/utils/content-image-url.ts`, which does keep a
 * `variant=upscaled` branch for its download portal.
 */

const GET_IMAGE_RE = /\/api\/get_image\/[^/?#]+\/([^/?#]+)$/;
const GET_UPSCALED_RE = /\/api\/get_upscaled_image\/[^/?#]+\/([^/?#]+)$/;

export function toV1ContentImageUrl(url: string): string {
  if (!url) return url;
  const splitAt = url.search(/[?#]/);
  const path = splitAt === -1 ? url : url.slice(0, splitAt);
  const suffix = splitAt === -1 ? '' : url.slice(splitAt);

  const upscaled = GET_UPSCALED_RE.exec(path);
  if (upscaled) {
    return (
      path.replace(GET_UPSCALED_RE, `/api/v1/content/images/${upscaled[1]}`) +
      suffix
    );
  }

  const image = GET_IMAGE_RE.exec(path);
  if (image) {
    return (
      path.replace(GET_IMAGE_RE, `/api/v1/content/images/${image[1]}`) + suffix
    );
  }

  return url;
}

/**
 * Append the display-resize tier (`width`/`quality`) to a v1 content URL.
 *
 * The tier policy lives at the call site's surface level (thumbnail vs
 * full display); print and download paths must NOT use this — originals
 * only. Non-v1 URLs pass through untouched and an already-sized URL is
 * returned as-is (idempotent).
 */
export function contentImageUrlWithSize(
  url: string,
  width: number,
  quality?: number
): string {
  if (!url || !url.includes('/api/v1/content/') || /[?&]width=/.test(url)) {
    return url;
  }
  const joiner = url.includes('?') ? '&' : '?';
  const qualitySuffix = quality === undefined ? '' : `&quality=${quality}`;
  return `${url}${joiner}width=${width}${qualitySuffix}`;
}
